"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import {
  User,
  Check,
  CheckCheck,
  ShieldCheck,
  Users,
  FileText,
  Mic,
  MapPin,
  CornerUpLeft,
  Send,
  Paperclip,
  Smile,
  Menu,
  X,
  Reply,
  Volume2,
  VolumeX,
  ThumbsUp,
  Heart,
  Laugh,
  Frown,
  Angry,
  ImageIcon,
  Loader,
  ChevronLeft,
  MoreVertical,
  Info,
  Search,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  getWhatsAppChatListAction,
  getWhatsAppChatShortcutAction,
  sendWhatsAppMessageAction,
  updateUnreadMessageCountAction,
  sendMessageReactionAction,
  uploadImageAction,
} from "../../services/action/common";
import socket from "../socket/socket";
import Picker from "emoji-picker-react";
import { formatMessageTime } from "../utils/functions/functions";
import { useLocation } from "react-router-dom";
import DashboardLayout from "../layouts/DashboardLayout";
import { handlePopupModalAction } from "../../services/action/event";

const MessageStatus = ({ status }) => {
  if (!status) return null;

  switch (status) {
    case "sent":
      return <Check className="inline-block w-3 h-3 text-gray-400" />;
    case "delivered":
      return <CheckCheck className="inline-block w-3 h-3 text-gray-400" />;
    case "read":
      return <CheckCheck className="inline-block w-3 h-3 text-blue-400" />;
    default:
      return null;
  }
};

const ClientIcon = ({ type, className = "w-4 h-4" }) => {
  switch (type) {
    case "Enterprise":
      return <ShieldCheck className={className} />;
    case "Small Business":
      return <Users className={className} />;
    default:
      return <User className={className} />;
  }
};

const MessageReactions = ({ reactions, onClick }) => {
  if (!reactions || reactions.length === 0) return null;

  const reactionCounts = reactions.reduce((acc, reaction) => {
    acc[reaction.reactionType] = (acc[reaction.reactionType] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="flex items-center gap-1 mt-1">
      {Object.entries(reactionCounts).map(([type, count]) => (
        <button
          key={type}
          onClick={() =>
            onClick(reactions.filter((r) => r.reactionType === type))
          }
          className="inline-flex items-center gap-1 bg-white/10 hover:bg-white/20 text-inherit rounded-full px-2 py-0.5 text-xs transition-colors duration-200"
        >
          {type} <span className="font-medium">{count}</span>
        </button>
      ))}
    </div>
  );
};

const MessageContent = ({
  message,
  replyMessage,
  onReplyClick,
  onReply,
  onReact,
  setReactionDetails,
}) => {
  const [showReactions, setShowReactions] = useState(false);
  const [imageError, setImageError] = useState(false);
  const reactionsRef = useRef(null);

  const handleReact = (reactionType) => {
    onReact(message._id, reactionType);
    setShowReactions(false);
  };

  const handleReactionClick = (reactions) => {
    setReactionDetails(reactions);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        reactionsRef.current &&
        !reactionsRef.current.contains(event.target)
      ) {
        setShowReactions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="break-words relative flex flex-col gap-1">
      {replyMessage && (
        <div
          className="bg-gray-200 p-2 rounded-lg mb-2 text-sm cursor-pointer hover:bg-gray-300 transition-colors duration-200 flex items-start"
          onClick={(e) => {
            e.stopPropagation();
            onReplyClick(replyMessage._id);
          }}
        >
          <CornerUpLeft
            size={16}
            className="text-gray-500 mr-2 mt-1 flex-shrink-0"
          />
          <div className="overflow-hidden">
            <p className="font-medium text-gray-700 truncate">
              Replying to: {replyMessage.sender?.name}
            </p>
            <p className="text-gray-600 truncate">{replyMessage.content}</p>
          </div>
        </div>
      )}
      {(() => {
        switch (message.messageType) {
          case "text":
            return (
              <p className="text-base leading-relaxed break-words">
                {message.content}
              </p>
            );
          case "image":
            return (
              <div className="max-w-sm">
                <div
                  className={`relative ${
                    imageError ? "image-loading-error" : ""
                  }`}
                >
                  <img
                    src={
                      message.mediaUrl ||
                      "/placeholder.svg?height=200&width=300" ||
                      "/placeholder.svg"
                    }
                    alt="Image"
                    className="w-full h-auto rounded-lg mb-2 max-h-60 object-cover"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src =
                        "/placeholder.svg?height=200&width=300";
                      setImageError(true);
                    }}
                  />
                  {message.mediaUrl && !imageError && (
                    <a
                      href={message.mediaUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 opacity-0 hover:opacity-100 transition-opacity duration-200"
                    >
                      <span className="text-white text-sm font-medium">
                        View Full Image
                      </span>
                    </a>
                  )}
                </div>
                {message.content && (
                  <p className="text-sm mt-1 break-words">{message.content}</p>
                )}
              </div>
            );
          case "document":
            return (
              <div className="flex items-center">
                <FileText className="mr-2 flex-shrink-0" />
                <a
                  href={message.mediaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:underline truncate"
                >
                  {message.fileName || "Document"}
                </a>
              </div>
            );
          case "audio":
            return (
              <div className="flex items-center">
                <Mic className="mr-2 flex-shrink-0" />
                <audio controls src={message.mediaUrl} className="max-w-full" />
              </div>
            );
          case "video":
            return (
              <div>
                <video
                  controls
                  src={message.mediaUrl}
                  className="max-w-full h-auto rounded-lg mb-2 max-h-60"
                />
                {message.content && (
                  <p className="text-sm break-words">{message.content}</p>
                )}
              </div>
            );
          case "location":
            return (
              <div className="flex items-center">
                <MapPin className="mr-2 flex-shrink-0" />
                <span className="truncate">{message.content}</span>
              </div>
            );
          case "emoji":
            return <span className="text-4xl">{message.emojiUnicode}</span>;
          case "file":
            return (
              <div className="flex items-center">
                <FileText className="mr-2 flex-shrink-0" />
                <a
                  href={message.fileUrl || message.mediaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:underline truncate"
                >
                  {message.text || message.fileName || "File"}
                </a>
              </div>
            );
          default:
            return (
              <p className="text-base leading-relaxed break-words">
                {message.content}
              </p>
            );
        }
      })()}
      <div className="flex items-center gap-2 mt-1">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onReply(message);
          }}
          className="p-1 text-gray-500 hover:text-gray-700 transition-colors duration-200"
          aria-label="Reply to message"
        >
          <Reply size={14} />
        </button>
        <button
          onClick={() => setShowReactions(!showReactions)}
          className="p-1 text-gray-500 hover:text-gray-700 transition-colors duration-200"
          aria-label="React to message"
        >
          <Smile size={14} />
        </button>
      </div>
      {showReactions && (
        <div
          ref={reactionsRef}
          className="absolute bottom-full left-0 bg-white shadow-lg rounded-lg p-2 flex space-x-2 z-10"
        >
          <button
            onClick={() => handleReact("👍")}
            className="hover:bg-gray-100 p-1 rounded text-gray-700 hover:text-blue-500"
            aria-label="Thumbs up reaction"
          >
            <ThumbsUp size={16} />
          </button>
          <button
            onClick={() => handleReact("❤️")}
            className="hover:bg-gray-100 p-1 rounded text-gray-700 hover:text-red-500"
            aria-label="Heart reaction"
          >
            <Heart size={16} />
          </button>
          <button
            onClick={() => handleReact("😂")}
            className="hover:bg-gray-100 p-1 rounded text-gray-700 hover:text-yellow-500"
            aria-label="Laugh reaction"
          >
            <Laugh size={16} />
          </button>
          <button
            onClick={() => handleReact("😢")}
            className="hover:bg-gray-100 p-1 rounded text-gray-700 hover:text-blue-500"
            aria-label="Sad reaction"
          >
            <Frown size={16} />
          </button>
          <button
            onClick={() => handleReact("😠")}
            className="hover:bg-gray-100 p-1 rounded text-gray-700 hover:text-red-500"
            aria-label="Angry reaction"
          >
            <Angry size={16} />
          </button>
        </div>
      )}
      <MessageReactions
        reactions={message.reactions}
        onClick={handleReactionClick}
      />
    </div>
  );
};

const ReactionDetails = ({ reactions, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-4 max-w-sm w-full max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4 sticky top-0 bg-white pb-2 border-b">
          <h3 className="text-lg font-semibold">Reactions</h3>
          <button
            onClick={onClose}
            className="p-1 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100"
            aria-label="Close reactions panel"
          >
            <X size={20} />
          </button>
        </div>
        <div className="space-y-3">
          {reactions.map((reaction, index) => (
            <div key={index} className="flex items-start justify-between">
              <div>
                <p className="font-medium text-sm">{reaction.sender.name}</p>
                <p className="text-xs text-gray-500">
                  {formatMessageTime(reaction.timestamp)}
                </p>
              </div>
              <span className="text-lg">{reaction.reactionType}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const scrollToMessage = (messageId) => {
  const messageElement = document.getElementById(`message-${messageId}`);
  if (messageElement) {
    messageElement.scrollIntoView({ behavior: "smooth", block: "center" });
    messageElement.classList.add("bg-blue-100");
    setTimeout(() => messageElement.classList.remove("bg-blue-100"), 2000);
  }
};

export default function SalesTeamChat() {
  const [selectedClient, setSelectedClient] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [inputMessage, setInputMessage] = useState("");
  const [showSidebar, setShowSidebar] = useState(true);
  const [replyingTo, setReplyingTo] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showClientInfo, setShowClientInfo] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [messageType, setMessageType] = useState("active");
  // active, history, template

  const location = useLocation();

  const getChatList = (conversationId) => {
    dispatch(
      getWhatsAppChatListAction({
        data: conversationId,
        cb: (res) => {
          setSelectedClient(res?.data?.clientDetail?._id);
        },
      })
    );
    dispatch(
      updateUnreadMessageCountAction({
        data: { conversationId: conversationId.conversationId },
      })
    );
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const conversationId = queryParams.get("conversationId");

    if (conversationId) {
      getChatList({ conversationId });
    }
  }, [location.search]);

  const { whatsappClient, whatsappChat } = useSelector((state) => ({
    ...state.commonReducer,
  }));
  const messagesEndRef = useRef(null);
  const messageContainerRef = useRef(null);
  const audioRef = useRef(new Audio("/assets/seen.wav"));
  const [totalUnreadMessages, setTotalUnreadMessages] = useState(0);
  const [reactionDetails, setReactionDetails] = useState(null);
  const [fileAttachments, setFileAttachments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const inputRef = useRef(null);

  const dispatch = useDispatch();

  const checkIfAtBottom = useCallback(() => {
    if (!messageContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } =
      messageContainerRef.current;
    // Use a larger threshold (30px) to make it more reliable
    const scrolledToBottom = scrollHeight - scrollTop - clientHeight < 30;

    // Only log when the state changes to avoid console spam
    if (isAtBottom !== scrolledToBottom) {
      console.log("Scroll position changed:", {
        scrolledToBottom,
        scrollTop,
        scrollHeight,
        clientHeight,
      });
    }

    setIsAtBottom(scrolledToBottom);
  }, [isAtBottom]);

  const toggleSidebar = () => {
    setShowSidebar(!showSidebar);
  };

  const toggleClientInfo = () => {
    setShowClientInfo(!showClientInfo);
  };

  const getShortcutChats = () => {
    dispatch(getWhatsAppChatShortcutAction({ data: { messageType } }));
  };

  useEffect(() => {
    socket.connect();

    socket.on("new_whatsapp_message", (data) => {
      if (data.conversationId === whatsappChat?.clientDetail?.conversationId) {
        dispatch(
          getWhatsAppChatListAction({
            data: { conversationId: data.conversationId },
          })
        );
        dispatch(
          updateUnreadMessageCountAction({
            data: { conversationId: data.conversationId },
          })
        );
        if (soundEnabled) {
          audioRef.current
            .play()
            .catch((err) => console.log("Audio play error:", err));
        }
        if (isAtBottom) {
          setTimeout(scrollToBottom, 100); // Add a small delay to ensure new message is rendered
        }
      }
      getShortcutChats();
    });

    socket.on("message_status_update", (data) => {
      if (data.conversationId === whatsappChat?.clientDetail?.conversationId) {
        dispatch(
          getWhatsAppChatListAction({
            data: { conversationId: data.conversationId },
          })
        );
      }
    });

    socket.on("client_status_update", () => {
      getShortcutChats();
    });

    socket.on("new_whatsapp_reaction", (data) => {
      if (data.conversationId === whatsappChat?.clientDetail?.conversationId) {
        dispatch(
          getWhatsAppChatListAction({
            data: { conversationId: data.conversationId },
          })
        );
      }
    });

    return () => {
      socket.off("new_whatsapp_message");
      socket.off("message_status_update");
      socket.off("client_status_update");
      socket.off("new_whatsapp_reaction");
      socket.disconnect();
    };
  }, [
    dispatch,
    whatsappChat?.clientDetail?.conversationId,
    soundEnabled,
    isAtBottom,
  ]);

  useEffect(() => {
    getShortcutChats();
  }, [dispatch, messageType]);

  useEffect(() => {
    const total =
      whatsappClient?.reduce(
        (acc, client) => acc + (client.unreadCount || 0),
        0
      ) || 0;
    setTotalUnreadMessages(total);
  }, [whatsappClient]);

  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      // Use scrollIntoView with behavior: "auto" for immediate scrolling
      messagesEndRef.current.scrollIntoView({ behavior: "auto" });
      setIsAtBottom(true);
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [scrollToBottom]);

  useEffect(() => {
    if (selectedClient && whatsappChat?.chat?.length > 0) {
      scrollToBottom();
    }
  }, [selectedClient, scrollToBottom, whatsappChat?.chat?.length]);

  useEffect(() => {
    if (whatsappChat?.clientDetail?.conversationId) {
      dispatch(
        updateUnreadMessageCountAction({
          data: { conversationId: whatsappChat.clientDetail.conversationId },
        })
      );
    }
  }, [whatsappChat?.clientDetail?.conversationId, dispatch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target)
      ) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const messageContainer = messageContainerRef.current;
    if (messageContainer) {
      // Initial check
      checkIfAtBottom();

      const handleScroll = () => {
        checkIfAtBottom();
      };

      messageContainer.addEventListener("scroll", handleScroll);
      return () => messageContainer.removeEventListener("scroll", handleScroll);
    }
  }, [checkIfAtBottom]);

  const handleEmojiSelect = (emoji) => {
    setInputMessage((prevMessage) => prevMessage + emoji.emoji);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const uploadAttachment = (e) => {
    setIsUploading(true);
    setUploadError(null);

    const callBackAttachment = (res) => {
      if (res?.imageUrls) {
        const newAttachments = res.imageUrls.map((url) => ({
          url,
          name: url.split("/").pop() || "Untitled",
          type: getFileType(url),
        }));
        setFileAttachments((prev) => [...prev, ...newAttachments]);
      } else if (res?.error) {
        setUploadError(res.error);
        console.error("Upload error:", res.error);
      }
      setIsUploading(false);
    };

    const formData = new FormData();
    if (e.target.files && e.target.files.length > 0) {
      // Check file sizes before uploading
      let totalSize = 0;
      const maxSize = 16 * 1024 * 1024; // 16MB max for WhatsApp

      for (let i = 0; i < e.target.files.length; i++) {
        totalSize += e.target.files[i].size;
        formData.append("images", e.target.files[i]);
      }

      if (totalSize > maxSize) {
        setUploadError("File size exceeds the 16MB limit for WhatsApp");
        setIsUploading(false);
        e.target.value = "";
        return;
      }

      dispatch(uploadImageAction({ data: formData, cb: callBackAttachment }));
      e.target.value = "";
    } else {
      setIsUploading(false);
    }
  };

  const getFileType = (url) => {
    const extension = url.split(".").pop().toLowerCase();
    const imageTypes = ["jpg", "jpeg", "png", "gif", "webp"];
    const videoTypes = ["mp4", "webm", "mov"];
    const audioTypes = ["mp3", "wav", "ogg"];

    if (imageTypes.includes(extension)) return "image";
    if (videoTypes.includes(extension)) return "video";
    if (audioTypes.includes(extension)) return "audio";
    return "file";
  };

  const handleFileChange = (e) => {
    uploadAttachment(e);
  };

  const handleImageChange = (e) => {
    uploadAttachment(e);
  };

  const removeAttachment = (index) => {
    setFileAttachments((prevAttachments) =>
      prevAttachments.filter((_, i) => i !== index)
    );
  };

  const sendMessage = () => {
    if (inputMessage.trim() === "" && fileAttachments.length === 0) return;
    setUploadError(null);

    // If there are multiple attachments, send them as separate messages
    if (fileAttachments.length > 0) {
      // Send each attachment as a separate message
      fileAttachments.forEach((file, index) => {
        const isLast = index === fileAttachments.length - 1;

        const messageData = {
          conversationId: whatsappChat?.clientDetail?.conversationId,
          content: {
            type: file.type || "file",
            url: file.url,
            filename: file.name,
            caption: isLast && inputMessage.trim() ? inputMessage : "",
          },
          replyTo: index === 0 ? replyingTo?._id || null : null, // Only include reply for first message
        };

        dispatch(sendWhatsAppMessageAction({ data: messageData }));
      });
    } else {
      // Text-only message
      const messageData = {
        conversationId: whatsappChat?.clientDetail?.conversationId,
        content: {
          type: "text",
          message: inputMessage,
        },
        replyTo: replyingTo?._id || null,
      };

      dispatch(sendWhatsAppMessageAction({ data: messageData }));
    }

    setInputMessage("");
    setFileAttachments([]);
    setReplyingTo(null);
    setShowEmojiPicker(false);
    setTimeout(scrollToBottom, 100); // Always scroll to bottom after sending
    setIsAtBottom(true);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleReply = (message) => {
    setReplyingTo(message);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  useEffect(() => {
    if (messageContainerRef.current) {
      messageContainerRef.current.addEventListener("click", handleMessageClick);
    }
    return () => {
      if (messageContainerRef.current) {
        messageContainerRef.current.removeEventListener(
          "click",
          handleMessageClick
        );
      }
    };
  }, []);

  const handleMessageClick = (event) => {
    const messageElement = event.target.closest('[id^="message-"]');
    if (messageElement) {
      const messageId = messageElement.id.replace("message-", "");
      scrollToMessage(messageId);
    }
  };

  const toggleSound = () => {
    setSoundEnabled(!soundEnabled);
  };

  const handleReact = (messageId, reactionType) => {
    dispatch(
      sendMessageReactionAction({
        data: {
          messageId,
          reactionType,
          conversationId: whatsappChat?.clientDetail?.conversationId,
        },
      })
    );
  };

  // Add this useEffect to ensure chat scrolls to bottom when messages are loaded
  useEffect(() => {
    if (whatsappChat?.chat?.length > 0) {
      // Use setTimeout to ensure DOM is fully rendered
      setTimeout(scrollToBottom, 100);
    }
  }, [whatsappChat?.chat, scrollToBottom]);

  return (
    <DashboardLayout>
      <div className="flex h-[100vh] bg-gray-100">
        <style jsx global>{`
          .image-loading-error::before {
            content: "Image failed to load";
            display: flex;
            align-items: center;
            justify-content: center;
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: #f3f4f6;
            color: #6b7280;
            font-size: 0.875rem;
            text-align: center;
          }
          .chat-background {
            background-color: #f0f2f5;
            background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fillRule='evenodd'%3E%3Cg fill='%23ffffff' fillOpacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
          }
          .emoji-picker-react .emoji-group:before {
            font-size: 16px;
            padding: 12px;
          }
          .emoji-picker-react .emoji {
            width: 32px;
            height: 32px;
          }

          /* Custom scrollbar for webkit browsers */
          ::-webkit-scrollbar {
            width: 6px;
            height: 6px;
          }
          ::-webkit-scrollbar-track {
            background: transparent;
          }
          ::-webkit-scrollbar-thumb {
            background: rgba(0, 0, 0, 0.2);
            border-radius: 3px;
          }
          ::-webkit-scrollbar-thumb:hover {
            background: rgba(0, 0, 0, 0.3);
          }

          /* Message highlight animation */
          @keyframes highlight {
            0% {
              background-color: rgba(59, 130, 246, 0.3);
            }
            100% {
              background-color: transparent;
            }
          }
          .message-highlight {
            animation: highlight 2s ease-out;
          }

          /* Improve touch targets for mobile */
          @media (max-width: 640px) {
            button {
              min-height: 36px;
              min-width: 36px;
            }
          }
        `}</style>

        {/* Sidebar */}
        <div
          className={`${
            showSidebar ? "translate-x-0" : "-translate-x-full"
          } transform transition-transform duration-300 fixed md:relative z-20 w-full md:w-80 lg:w-96 bg-white border-r border-gray-200 h-full overflow-hidden flex flex-col`}
        >
          <div className="p-2 bg-[#001B3D] text-white flex justify-between items-center">
            <button
              onClick={() =>
                dispatch(handlePopupModalAction({ isOpenSidebar: true }))
              }
              className="p-2 hover:text-white rounded-md hover:bg-[#0A2547] transition-colors lg:hidden"
            >
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="text-lg font-semibold tracking-tight text-white truncate">
              Clickinpedia Sales Chat
              {totalUnreadMessages > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs font-medium rounded-full h-5 min-w-[20px] inline-flex items-center justify-center px-1.5">
                  {totalUnreadMessages}
                </span>
              )}
            </h1>
            <button
              onClick={toggleSidebar}
              className="md:hidden p-1.5 hover:bg-[#002857] rounded-lg"
              aria-label="Close sidebar"
            >
              <X size={18} />
            </button>
          </div>

          {/* Search bar for clients */}
          <div className="px-4 py-2 border-b border-gray-100">
            <div className="relative">
              <input
                style={{
                  paddingLeft: "29px!important",
                  paddingRight: "2px !important",
                }}
                type="search"
                placeholder="Search clients..."
                className="w-full p-2 pl-8 pr-4 rounded-lg bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#001B3D] focus:border-transparent"
              />
              <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            </div>
          </div>

          {/* Tabs Navigation */}
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setMessageType("active")}
              className={`flex-1 py-3 px-4 text-sm font-medium ${
                messageType === "active"
                  ? "text-[#001B3D] border-b-2 border-[#001B3D]"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
              }`}
            >
              Active
            </button>
            <button
              onClick={() => setMessageType("history")}
              className={`flex-1 py-3 px-4 text-sm font-medium ${
                messageType === "history"
                  ? "text-[#001B3D] border-b-2 border-[#001B3D]"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
              }`}
            >
              History
            </button>
            <button
              onClick={() => setMessageType("template")}
              className={`flex-1 py-3 px-4 text-sm font-medium ${
                messageType === "template"
                  ? "text-[#001B3D] border-b-2 border-[#001B3D]"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
              }`}
            >
              Template
            </button>
          </div>

          {/* Client List */}
          <div className="overflow-y-auto flex-1">
            {whatsappClient?.map((client) => (
              <motion.div
                key={client?.clientId}
                className={`p-3 border-b border-gray-100 cursor-pointer ${
                  selectedClient === client.clientId ? "bg-blue-50" : ""
                }`}
                onClick={() => {
                  getChatList({ conversationId: client?.conversationId });
                  if (window.innerWidth < 768) {
                    setShowSidebar(false);
                  }
                }}
                whileHover={{ backgroundColor: "#f0f9ff" }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center gap-3">
                  <div className="relative flex-shrink-0">
                    <div className="w-10 h-10 bg-[#001B3D] rounded-full flex items-center justify-center text-white">
                      <ClientIcon type={client?.userType} className="w-5 h-5" />
                    </div>
                    {client?.isOnline && (
                      <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-0.5">
                      <h2 className="text-base font-semibold text-gray-900 tracking-tight truncate">
                        {client?.name}
                      </h2>
                      <span className="text-xs text-gray-500 tabular-nums">
                        {formatMessageTime(client?.timestamp)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 truncate mb-0.5">
                      {client?.lastMessage}
                    </p>
                    <div className="flex items-center gap-1">
                      <User size={12} className="text-gray-400" />
                      <span className="text-xs text-gray-500 font-medium truncate">
                        +{client?.number}
                      </span>
                    </div>
                  </div>
                  {client?.unreadCount > 0 && (
                    <span className="bg-[#001B3D] text-white text-xs font-medium rounded-full h-5 min-w-[20px] flex items-center justify-center px-1.5">
                      {client?.unreadCount}
                    </span>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col relative overflow-hidden">
          <AnimatePresence mode="wait">
            {selectedClient ? (
              <motion.div
                key={selectedClient}
                className="flex-1 flex flex-col h-full"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {/* Chat Header */}
                <div className="p-2 bg-[#001B3D] text-white flex items-center gap-2 shadow-sm">
                  <button
                    onClick={toggleSidebar}
                    className="md:hidden p-1.5 hover:bg-[#002857] rounded-lg"
                    aria-label="Open sidebar"
                  >
                    <Menu size={18} />
                  </button>
                  <button
                    onClick={() => {
                      setSelectedClient(null);
                      setShowSidebar(true);
                    }}
                    className="md:hidden p-1.5 hover:bg-[#002857] rounded-lg"
                    aria-label="Back to client list"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div className="relative">
                      <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center text-[#001B3D]">
                        <ClientIcon
                          type={
                            whatsappChat?.clientDetail?.userType || "Enterprise"
                          }
                          className="w-4 h-4"
                        />
                      </div>
                      {whatsappChat?.clientDetail?.isOnline && (
                        <span className="absolute bottom-0 right-0 w-2 h-2 bg-green-500 rounded-full border-2 border-[#001B3D]"></span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-sm font-semibold tracking-tight truncate text-white">
                        {whatsappChat?.clientDetail?.name}
                      </h2>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-blue-100 truncate">
                          +{whatsappChat?.clientDetail?.number}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={toggleSound}
                        className="p-1.5 hover:bg-[#002857] rounded-full"
                        aria-label={
                          soundEnabled
                            ? "Mute notifications"
                            : "Unmute notifications"
                        }
                      >
                        {soundEnabled ? (
                          <Volume2 size={16} />
                        ) : (
                          <VolumeX size={16} />
                        )}
                      </button>
                      <button
                        onClick={toggleClientInfo}
                        className="p-1.5 hover:bg-[#002857] rounded-full"
                        aria-label="Client information"
                      >
                        <Info size={16} />
                      </button>
                      <button
                        className="p-1.5 hover:bg-[#002857] rounded-full md:hidden"
                        aria-label="More options"
                      >
                        <MoreVertical size={16} />
                      </button>
                    </div>
                  </div>
                  <div className="hidden md:flex items-center gap-2">
                    <span className="text-xs font-medium text-blue-100">
                      My number: +{whatsappChat?.clientDetail?.companyNumber}
                    </span>
                  </div>
                </div>

                {/* Client Info Sidebar */}
                {showClientInfo && (
                  <div className="absolute top-16 right-0 bottom-0 w-72 bg-white shadow-lg border-l border-gray-200 z-10 overflow-y-auto">
                    <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                      <h3 className="font-semibold text-gray-900">
                        Client Info
                      </h3>
                      <button
                        onClick={toggleClientInfo}
                        className="p-1 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100"
                        aria-label="Close client info"
                      >
                        <X size={18} />
                      </button>
                    </div>
                    <div className="p-4">
                      <div className="flex flex-col items-center mb-4">
                        <div className="w-20 h-20 bg-[#001B3D] rounded-full flex items-center justify-center text-white mb-2">
                          <ClientIcon
                            type={
                              whatsappChat?.clientDetail?.userType ||
                              "Enterprise"
                            }
                            className="w-10 h-10"
                          />
                        </div>
                        <h4 className="font-semibold text-gray-900">
                          {whatsappChat?.clientDetail?.name}
                        </h4>
                        <p className="text-sm text-gray-600">
                          +{whatsappChat?.clientDetail?.number}
                        </p>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <h5 className="text-xs font-medium text-gray-500 uppercase mb-1">
                            Client Type
                          </h5>
                          <p className="text-sm text-gray-900">
                            {whatsappChat?.clientDetail?.userType ||
                              "Enterprise"}
                          </p>
                        </div>
                        <div>
                          <h5 className="text-xs font-medium text-gray-500 uppercase mb-1">
                            Email
                          </h5>
                          <p className="text-sm text-gray-900">
                            {whatsappChat?.clientDetail?.email ||
                              "Not available"}
                          </p>
                        </div>
                        <div>
                          <h5 className="text-xs font-medium text-gray-500 uppercase mb-1">
                            Company
                          </h5>
                          <p className="text-sm text-gray-900">
                            {whatsappChat?.clientDetail?.company ||
                              "Not available"}
                          </p>
                        </div>
                        <div>
                          <h5 className="text-xs font-medium text-gray-500 uppercase mb-1">
                            First Contact
                          </h5>
                          <p className="text-sm text-gray-900">
                            {whatsappChat?.clientDetail?.firstContact ||
                              "Not available"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Messages Area */}
                <div
                  ref={messageContainerRef}
                  className="flex-1 overflow-y-auto p-4 md:p-6 chat-background relative"
                >
                  {whatsappChat?.chat?.map((message) => {
                    const replyMessage = message.replyTo
                      ? whatsappChat?.chat.find(
                          (m) => m._id === message.replyTo?._id
                        )
                      : null;
                    const onReplyClick = (messageId) => {
                      scrollToMessage(messageId);
                    };
                    return (
                      <motion.div
                        key={message._id}
                        id={`message-${message._id}`}
                        className={`mb-4 flex ${
                          message.sender?.userType === "CLIENT"
                            ? "justify-start"
                            : "justify-end"
                        }`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <div
                          className={`max-w-[85%] md:max-w-md lg:max-w-lg p-3 rounded-lg shadow-sm ${
                            message.sender?.userType === "CLIENT"
                              ? "bg-white text-gray-900"
                              : "bg-[#001B3D] text-white"
                          } relative`}
                        >
                          <div className="flex flex-col gap-1">
                            <p className="text-sm font-medium">
                              {message.sender?.userType === "CLIENT"
                                ? message?.sender?.name
                                : `${
                                    message?.sender?.name
                                  } (${message.sender?.userType?.toLowerCase()})`}
                            </p>
                            <MessageContent
                              message={message}
                              replyMessage={replyMessage}
                              onReplyClick={onReplyClick}
                              onReply={handleReply}
                              onReact={handleReact}
                              setReactionDetails={setReactionDetails}
                            />
                            <div className="flex items-center gap-2 text-xs opacity-75">
                              <span className="tabular-nums">
                                {formatMessageTime(message.timestamp)}
                              </span>
                              {message.sender?.userType !== "CLIENT" && (
                                <MessageStatus status={message.status} />
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}

                  {/* Always visible down arrow button */}
                  <button
                    onClick={scrollToBottom}
                    className="fixed bottom-24 right-6 z-50 p-3 bg-[#001B3D] text-white rounded-full shadow-xl hover:bg-[#002857] transition-all duration-200 flex items-center justify-center"
                    aria-label="Scroll to bottom"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </button>

                  <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <div className="px-3 py-2 bg-white border-t border-gray-200">
                  {replyingTo && (
                    <div className="mb-2 p-2 bg-gray-100 rounded-lg flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-700 truncate">
                          Replying to:
                        </p>
                        <p className="text-sm text-gray-600 truncate">
                          {replyingTo.content}
                        </p>
                      </div>
                      <button
                        onClick={() => setReplyingTo(null)}
                        className="text-gray-500 hover:text-gray-700 ml-2"
                        aria-label="Cancel reply"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  )}
                  {uploadError && (
                    <div className="mb-2 p-2 bg-red-50 text-red-600 rounded-lg flex justify-between items-start">
                      <p className="text-sm">{uploadError}</p>
                      <button
                        onClick={() => setUploadError(null)}
                        className="text-red-500 hover:text-red-700 ml-2"
                        aria-label="Dismiss error"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  )}
                  {fileAttachments.length > 0 && (
                    <div className="mb-2 p-2 bg-gray-100 rounded-lg">
                      <p className="text-sm font-medium text-gray-700">
                        Attachments:
                      </p>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {fileAttachments.map((file, index) => (
                          <div
                            key={`file-${index}`}
                            className="flex items-center bg-white px-2 py-1 rounded border border-gray-200"
                          >
                            <span className="text-xs text-gray-600 truncate max-w-[150px]">
                              {file.name}
                            </span>
                            <button
                              onClick={() => removeAttachment(index)}
                              className="text-red-500 hover:text-red-700 ml-2"
                              aria-label={`Remove ${file.name}`}
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => fileInputRef.current.click()}
                      className="p-2 text-gray-500 hover:text-[#001B3D] focus:outline-none rounded-full hover:bg-gray-100"
                      disabled={isUploading}
                      aria-label="Attach file"
                    >
                      {isUploading ? (
                        <Loader className="animate-spin" size={20} />
                      ) : (
                        <Paperclip size={20} />
                      )}
                    </button>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      className="hidden"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar"
                    />
                    <button
                      onClick={() => imageInputRef.current.click()}
                      className="p-2 text-gray-500 hover:text-[#001B3D] focus:outline-none rounded-full hover:bg-gray-100"
                      disabled={isUploading}
                      aria-label="Attach image"
                    >
                      {isUploading ? (
                        <Loader className="animate-spin" size={20} />
                      ) : (
                        <ImageIcon size={20} />
                      )}
                    </button>
                    <input
                      type="file"
                      ref={imageInputRef}
                      onChange={handleImageChange}
                      className="hidden"
                      accept="image/*"
                    />
                    <div className="flex-1 relative">
                      <input
                        ref={inputRef}
                        type="text"
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Type a message"
                        className="w-full p-2 pl-3 pr-10 rounded-full bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#001B3D] text-gray-900"
                      />
                      <div
                        className="absolute right-2 top-1/2 transform -translate-y-1/2"
                        ref={emojiPickerRef}
                      >
                        <button
                          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                          className="p-1.5 text-gray-500 hover:text-[#001B3D] focus:outline-none rounded-full hover:bg-gray-100"
                          aria-label="Add emoji"
                        >
                          <Smile size={20} />
                        </button>
                        {showEmojiPicker && (
                          <div className="absolute bottom-12 right-0 z-50">
                            <Picker
                              onEmojiClick={handleEmojiSelect}
                              pickerStyle={{ width: "300px" }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={sendMessage}
                      disabled={
                        (!inputMessage.trim() &&
                          fileAttachments.length === 0) ||
                        isUploading
                      }
                      className="p-2.5 bg-[#001B3D] text-white rounded-full hover:bg-[#002857] focus:outline-none focus:ring-2 focus:ring-[#001B3D] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="Send message"
                    >
                      <Send size={20} />
                    </button>
                  </div>
                </div>
                {reactionDetails && (
                  <ReactionDetails
                    reactions={reactionDetails}
                    onClose={() => setReactionDetails(null)}
                  />
                )}
              </motion.div>
            ) : (
              <motion.div
                className="flex-1 flex items-center justify-center bg-gray-50 text-gray-500 p-4 text-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <div>
                  <button
                    onClick={toggleSidebar}
                    className="md:hidden mb-4 p-2 hover:bg-gray-100 rounded-lg"
                    aria-label="Open sidebar"
                  >
                    <Menu size={20} />
                  </button>
                  <div className="flex flex-col items-center gap-4">
                    <div className="w-16 h-16 bg-[#001B3D]/10 rounded-full flex items-center justify-center">
                      <User size={32} className="text-[#001B3D]" />
                    </div>
                    <p className="text-lg font-medium">
                      Select a client to start messaging
                    </p>
                    <p className="text-sm text-gray-400 max-w-md">
                      Choose a conversation from the sidebar to view messages
                      and respond to your clients
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </DashboardLayout>
  );
}
